import { useRef, useState } from "react";
import {
  Check,
  ChevronRight,
  FileText,
  Info,
  Paperclip,
  QrCode,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  EnvioApiError,
  MAX_BYTES,
  arquivoParaBase64,
  enviarAtestado,
  verificarArquivo,
  type Canal,
} from "@/lib/envio-colaborador-api";
import type { Acao, Estado, TipoAtestado } from "../estado";
import { Moldura, Titulo } from "./moldura";
import { medirEnvio, registrarFalhaEnvio } from "@/lib/sentry-envio";
import { compressImageFile } from "@/lib/image-compress";

/** Ler o arquivo no celular pode ser lento ou falhar: medido e, se falhar, reportado. */
function lerArquivo(f: File): Promise<string> {
  return medirEnvio("ler_arquivo", () => arquivoParaBase64(f), {
    tamanho_kb: Math.round(f.size / 1024),
    tipo: f.type,
  });
}

const ERRO_ARQUIVO = "Envie um PDF, JPG ou PNG de até 10 MB.";
const TIPOS_FISICO = "application/pdf,image/jpeg,image/png";

function CartaoTipo({
  titulo,
  sub,
  icone,
  onClick,
}: {
  titulo: string;
  sub: string;
  icone: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex min-h-[84px] items-center gap-3.5 rounded-2xl border-[1.5px] border-slate-300 bg-white p-4 text-left hover:border-blue-700"
    >
      <span
        aria-hidden="true"
        className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-700"
      >
        {icone}
      </span>
      <span className="flex flex-1 flex-col">
        <span className="text-[17px] font-bold">{titulo}</span>
        <span className="text-sm text-slate-600">{sub}</span>
      </span>
      <ChevronRight aria-hidden="true" className="h-5 w-5 text-slate-400" />
    </button>
  );
}

export function PassoAtestado({
  slug,
  canal,
  estado,
  despachar,
  onSessaoExpirada,
  onVoltar,
}: {
  slug: string;
  canal: Canal;
  estado: Estado;
  despachar: (a: Acao) => void;
  onSessaoExpirada: () => void;
  onVoltar?: () => void;
}) {
  const [verificando, setVerificando] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [termo, setTermo] = useState(false);
  const enviandoRef = useRef(false);
  const [erro, setErro] = useState<string | null>(null);
  const token = estado.token ?? "";

  function escolher(tipo: TipoAtestado) {
    setErro(null);
    despachar({ t: "escolherTipo", tipo });
  }

  async function anexar(ev: React.ChangeEvent<HTMLInputElement>) {
    const f = ev.target.files?.[0];
    ev.target.value = "";
    if (!f) return;
    setErro(null);
    const tipoOk =
      estado.tipo === "digital"
        ? f.type === "application/pdf"
        : TIPOS_FISICO.split(",").includes(f.type);
    if (!tipoOk || f.size > MAX_BYTES) {
      setErro(ERRO_ARQUIVO);
      return;
    }
    if (estado.tipo !== "digital") {
      // Mesma redução do Novo documento: foto de celular ia inteira para a IA e estourou a memória da
      // API (5 MB, 27/10). Só imagem: PDF (inclusive assinado) segue byte a byte.
      setVerificando(true);
      const arquivo = await compressImageFile(f).finally(() => setVerificando(false));
      despachar({ t: "arquivo", arquivo, digitalValido: null });
      return;
    }
    setVerificando(true);
    try {
      const r = await verificarArquivo(slug, token, await lerArquivo(f));
      despachar({ t: "arquivo", arquivo: f, digitalValido: r.digitalValido });
    } catch (e) {
      if (e instanceof EnvioApiError && e.status === 401)
        return onSessaoExpirada();
      if (!(e instanceof EnvioApiError)) registrarFalhaEnvio("ler_arquivo", e);
      setErro(
        e instanceof EnvioApiError
          ? e.message
          : "Não foi possível concluir agora. Tente de novo em instantes.",
      );
    } finally {
      setVerificando(false);
    }
  }

  async function enviar() {
    if (!estado.arquivo || enviandoRef.current) return;
    enviandoRef.current = true;
    setEnviando(true);
    setErro(null);
    try {
      const fileBase64 = await lerArquivo(estado.arquivo);
      const { protocolo } = await enviarAtestado(slug, token, {
        fileBase64,
        fileName: estado.arquivo.name,
        tipoEscolhido: estado.tipo === "digital" ? "digital" : "fisico",
        termoAceito: true,
      });
      despachar({ t: "enviado", protocolo });
    } catch (e) {
      if (e instanceof EnvioApiError && e.status === 401)
        return onSessaoExpirada();
      // 409: este envio já foi concluído (ex.: outra aba). Não há o que tentar de novo.
      if (e instanceof EnvioApiError && e.status === 409)
        return despachar({ t: "envioJaConcluido", mensagem: e.message });
      if (!(e instanceof EnvioApiError)) registrarFalhaEnvio("ler_arquivo", e);
      setErro(
        e instanceof EnvioApiError
          ? e.message
          : "Não foi possível concluir agora. Tente de novo em instantes.",
      );
    } finally {
      enviandoRef.current = false;
      setEnviando(false);
    }
  }

  const botaoEnviar = (
    <Button
      className="h-14 rounded-xl text-base"
      disabled={!estado.arquivo || !termo || verificando || enviando}
      onClick={enviar}
    >
      Enviar para o RH
    </Button>
  );
  const erroEl = erro && (
    <p role="alert" className="text-sm text-amber-800">
      {erro}
    </p>
  );
  const ocupado = verificando || enviando;
  const voltar = () => {
    if (ocupado) return;
    setErro(null);
    if (onVoltar) onVoltar();
    else despachar({ t: "voltar" });
  };

  if (!estado.tipo) {
    return (
      <Moldura
        canal={canal}
        passo={2}
        acao={botaoEnviar}
        onVoltar={voltar}
        voltarDesabilitado={ocupado}
      >
        <p className="text-[15px] text-slate-600">
          Olá, {estado.funcionario?.nome.split(" ")[0]}!
        </p>
        <Titulo className="mb-3 text-[26px]">Como é o seu atestado?</Titulo>
        <CartaoTipo
          titulo="Digital"
          sub="Tem QR code"
          icone={<QrCode className="h-6 w-6" />}
          onClick={() => escolher("digital")}
        />
        <CartaoTipo
          titulo="Físico"
          sub="Papel"
          icone={<FileText className="h-6 w-6" />}
          onClick={() => escolher("fisico")}
        />
      </Moldura>
    );
  }

  if (estado.arquivo) {
    const digital = estado.tipo === "digital" && estado.digitalValido === true;
    return (
      <Moldura
        canal={canal}
        passo={2}
        acao={botaoEnviar}
        onVoltar={voltar}
        voltarDesabilitado={ocupado}
      >
        <Titulo className="text-[26px]">
          {digital ? "Atestado digital" : "Atestado físico"}
        </Titulo>
        {estado.aviso && (
          <p
            role="status"
            className="flex gap-2.5 rounded-2xl border border-amber-200 bg-amber-50 p-3.5 text-sm text-amber-900"
          >
            <Info aria-hidden="true" className="mt-0.5 h-5 w-5 shrink-0" />
            {estado.aviso}
          </p>
        )}
        {!digital && !estado.aviso && (
          <p className="text-[15px] text-slate-600">
            Dá para ler tudo? Documento inteiro, carimbo e assinatura.
          </p>
        )}
        <div className="flex items-center gap-3.5 rounded-2xl border border-slate-200 p-3.5">
          <span
            aria-hidden="true"
            className="flex h-[72px] w-14 items-center justify-center rounded-lg border border-slate-300 bg-slate-100 text-xs font-bold text-slate-600"
          >
            {estado.arquivo.type === "application/pdf" ? "PDF" : "foto"}
          </span>
          <div className="flex flex-col gap-1.5">
            <span className="text-[15px] font-semibold break-all">
              {estado.arquivo.name}
            </span>
            {digital ? (
              <span className="flex items-center gap-1.5 self-start rounded-full bg-green-100 px-2.5 py-1 text-[13px] font-bold text-green-900">
                <Check aria-hidden="true" className="h-3.5 w-3.5" />
                Digital verificado
              </span>
            ) : (
              <span className="self-start rounded-full bg-indigo-100 px-2.5 py-1 text-[13px] font-bold text-indigo-900">
                Físico
              </span>
            )}
          </div>
        </div>
        <button
          type="button"
          className="min-h-11 self-center px-2 text-sm font-semibold text-blue-700"
          onClick={() => despachar({ t: "removerArquivo" })}
        >
          Trocar arquivo
        </button>
        {erroEl}
        <label className="mt-auto flex min-h-12 items-start gap-3 text-[15px] text-slate-800">
          <input
            type="checkbox"
            className="mt-0.5 h-6 w-6 shrink-0 accent-blue-700"
            checked={termo}
            onChange={(ev) => setTermo(ev.target.checked)}
          />
          Declaro que as informações e o documento que vou enviar são
          verdadeiros e não foram alterados.
        </label>
      </Moldura>
    );
  }

  const digital = estado.tipo === "digital";
  return (
    <Moldura
      canal={canal}
      passo={2}
      acao={botaoEnviar}
      onVoltar={voltar}
      voltarDesabilitado={ocupado}
    >
      <Titulo className="text-[26px]">
        {digital ? "Atestado digital" : "Atestado físico"}
      </Titulo>
      {digital ? (
        <ol className="flex flex-col gap-3.5">
          {[
            "Aponte a câmera do celular para o QR code do atestado",
            "No site que abrir, toque em Baixar PDF",
            "Volte aqui e anexe o PDF, sem editar",
          ].map((t, i) => (
            <li key={t} className="flex items-start gap-3 text-[15px]">
              <span
                aria-hidden="true"
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-blue-700 text-sm font-bold text-white"
              >
                {i + 1}
              </span>
              {t}
            </li>
          ))}
        </ol>
      ) : (
        <p className="text-[15px] text-slate-600">
          Anexe uma foto ou o arquivo escaneado do atestado.
        </p>
      )}
      <label className="mt-2 flex cursor-pointer flex-col items-center gap-1.5 rounded-2xl border-2 border-dashed border-slate-400 bg-slate-50 px-4 py-6 text-blue-700">
        <Paperclip aria-hidden="true" className="h-7 w-7" />
        <span className="text-base font-bold">
          {digital ? "Anexar PDF" : "Anexar arquivo"}
        </span>
        <span className="text-center text-[13px] text-slate-600">
          {digital
            ? "do celular ou do computador"
            : "do celular ou do computador · PDF, JPG ou PNG · até 10 MB"}
        </span>
        <input
          type="file"
          className="sr-only"
          aria-label={digital ? "Anexar PDF" : "Anexar arquivo"}
          accept={digital ? "application/pdf" : TIPOS_FISICO}
          onChange={anexar}
          disabled={verificando}
        />
      </label>
      {verificando && (
        <p className="text-center text-sm text-slate-600" role="status">
          Verificando o arquivo…
        </p>
      )}
      {!digital && (
        <ul className="mt-1 flex flex-col gap-2.5 text-sm text-slate-700">
          {[
            "Documento inteiro, com as 4 bordas",
            "Boa luz, sem reflexo",
            "Carimbo e assinatura legíveis",
          ].map((t) => (
            <li key={t} className="flex items-center gap-2.5">
              <Check aria-hidden="true" className="h-4 w-4 text-green-700" />
              {t}
            </li>
          ))}
        </ul>
      )}
      {erroEl}
    </Moldura>
  );
}
